// @flow

import React from 'react';
import trim from 'lodash/fp/trim';
import toString from 'lodash/fp/toString';
import classNames from 'classnames';
import type { Time } from 'type/type';

import './TimeInput.scss';

type TimeField = 'hour' | 'minutes' | 'seconds'

type Props = {
  time: Time,
  onChange: (time: Time) => void,
  maxHour?: number,
  maxMinutes?: number,
  maxSeconds?: number,
  hourPlaceholder?: string,
  minutesPlaceholder?: string,
  secondsPlaceholder?: string,
  formatHourOnBlur?: boolean,
  formatMinutesOnBlur?: boolean,
  formatSecondsOnBlur?: boolean,
  useMaxIfOverflow?: boolean,
  validateEmpty?: boolean,
}

const isArrowKey = (key: number) => key >= 37 && key <= 40;

const isCompositeEditKey = (key: number, metaKey: boolean, ctrlKey: boolean) => {
  const A = 65;
  const C = 67;
  const X = 88;
  const Z = 90;

  return (metaKey || ctrlKey) && (key === A || key === C || key === X || key === Z);
};

const isNumberKey = (key: number, shiftKey: boolean) => {
  const ZERO = 48;
  const NINE = 57;
  const NUM_PAD_ZERO = 96;
  const NUM_PAD_NINE = 105;

  if (!shiftKey) {
    if (key >= ZERO && key <= NINE) {
      return true;
    }
    if (key >= NUM_PAD_ZERO && key <= NUM_PAD_NINE) {
      return true;
    }
  }
  return false;
};

const TimeInput = ({
  time,
  onChange,
  maxHour = 23,
  maxMinutes = 59,
  maxSeconds = 59,
  hourPlaceholder,
  minutesPlaceholder,
  secondsPlaceholder,
  formatHourOnBlur,
  formatMinutesOnBlur,
  formatSecondsOnBlur,
  useMaxIfOverflow,
  validateEmpty,
}: Props) => {
  const { hour, minutes, seconds } = time;

  const notifyChange = (value: string, field: TimeField, max: number) => {
    const text = trim(value);
    const num = parseInt(text, 10);

    if (text.length <= `${max}`.length) {
      if (!text) {
        onChange({ ...time, [field]: value });
      } else if (Number.isNaN(num)) {
        onChange({ ...time, [field]: '' });
      } else {
        if (num >= 0 && num <= max) {
          onChange({ ...time, [field]: value });
        }
        if (num > max && useMaxIfOverflow) {
          onChange({ ...time, [field]: `${max}` });
        }
      }
    }
  };

  return (
    <div className="time-input">
      <div className="time-input__wrapper">
        <TimeNumberInput
          errored={!hour && !!validateEmpty}
          value={trim(toString(hour))}
          formatOnBlur={formatHourOnBlur}
          placeholder={hourPlaceholder}
          onChange={(value: string) => {
            notifyChange(value, 'hour', maxHour);
          }} />
        <TimeSeparator />
        <TimeNumberInput
          errored={!minutes && !!validateEmpty}
          value={trim(toString(minutes))}
          formatOnBlur={formatMinutesOnBlur}
          placeholder={minutesPlaceholder}
          onChange={(value: string) => {
            notifyChange(value, 'minutes', maxMinutes);
          }} />
        <TimeSeparator />
        <TimeNumberInput
          errored={!seconds && !!validateEmpty}
          value={trim(toString(seconds))}
          formatOnBlur={formatSecondsOnBlur}
          placeholder={secondsPlaceholder}
          onChange={(value: string) => {
            notifyChange(value, 'seconds', maxSeconds);
          }} />
      </div>
    </div>
  );
};

TimeInput.defaultProps = {
  maxHour: 23,
  maxMinutes: 59,
  maxSeconds: 59,
  hourPlaceholder: 'hh',
  minutesPlaceholder: 'mm',
  secondsPlaceholder: 'ss',
  formatHourOnBlur: true,
  formatMinutesOnBlur: true,
  formatSecondsOnBlur: true,
  useMaxIfOverflow: false,
  validateEmpty: false,
};

const TimeNumberInput = ({
  value,
  formatOnBlur,
  placeholder,
  onChange,
  errored,
}: {
  value: string,
  formatOnBlur: ?boolean,
  placeholder: ?string,
  onChange: (value: string) => void,
  errored: boolean,
}) => {
  return (
    <div className="time-input__number-input">
      <input
        className={classNames('input', {
          'input--errored': errored,
        })}
        type="number"
        value={value}
        placeholder={placeholder}
        onKeyDown={(event) => {
          const key = event.which || event.keyCode;
          const { shiftKey, metaKey, ctrlKey } = event;
          const numberKey = isNumberKey(key, shiftKey);
          const arrowKey = isArrowKey(key);
          const compositeEditKey = isCompositeEditKey(key, metaKey, ctrlKey);
          const deleteKey = key === 8;
          const tabKey = key === 9;

          if (!(numberKey || arrowKey || compositeEditKey || deleteKey || tabKey)) {
            event.preventDefault();
          }
        }}
        onBlur={(event) => {
          const { target } = event;
          const { validity } = target;
          // User can still input invalid chars on mobile using input method like pinyin
          // So we have to valiate the input here
          if (validity && validity.badInput) {
            // This looks bad, but we have to do this, because if the input is bad input,
            // the value of the input is an empty string so that onChange('') will not
            // trigger rerender and so we have to clear the input manually here
            target.value = '';
            onChange('');
          } else if (formatOnBlur && value.length === 1) {
            onChange(`0${value}`);
          }
        }}
        onChange={(event) => {
          onChange(event.target.value);
        }} />
    </div>
  );
};

const TimeSeparator = () => {
  return <div className="time-input__separator">:</div>;
};

export default TimeInput;
